import { BadRequestError, ConflictError, ForbiddenError, UnauthorizedError } from '@takeshape/errors';
import axios, { type AxiosError, type AxiosRequestConfig, type AxiosResponse, type Method } from 'axios';
import _get from 'lodash/get';
import { apiEndpoint } from '../../config.js';
import { getToken } from './get-token.js';

export function handleErrorCodes(error: AxiosError): Error {
  const { response } = error;

  if (response) {
    if (response.status === 400) {
      return new BadRequestError(_get(error, 'response.data.message'), response.data);
    }

    if (response.status === 409) {
      return new ConflictError(_get(error, 'response.data.message'), response.data);
    }

    if (response.status === 401) {
      return new UnauthorizedError(_get(error, 'response.data.message'));
    }

    if (response.status === 403) {
      return new ForbiddenError(_get(error, 'response.data.message'));
    }
  }

  return error;
}

export type ApiParams = {
  method: string;
  path?: string;
  url?: string;
  data?: any;
  params?: any;
};

export default async function api(params: ApiParams): Promise<AxiosResponse> {
  let url;
  if (params.path === undefined && params.url === undefined) {
    throw new Error('Must specify either URL or path');
  }
  if (params.path) {
    url = `${apiEndpoint}/${params.path}`;
  } else {
    url = params.url;
  }

  const requestParams: AxiosRequestConfig = {
    method: params.method as Method,
    responseType: 'json',
    url,
    withCredentials: false,
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
      'X-TakeShape-Token': getToken() ?? ''
    }
  };

  if (params.data) {
    requestParams.data = params.data;
  }

  if (params.params) {
    requestParams.params = params.params;
  }

  try {
    return await axios(requestParams);
  } catch (error) {
    throw handleErrorCodes(error);
  }
}
